import React, { useMemo, useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    Chip,
    CircularProgress,
    Alert,
    List,
    ListItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import {
    NotificationsOff as NotificationsOffIcon,
    LocalFireDepartment as FireIcon,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { PageHeader, ViewToggle } from '../../components/admin';
import type { ViewMode } from '../../components/admin';
import type { Dostupnost, DostupnostStav } from '../../api/JsdhNotifikaceService/generated/models/Dostupnost';
import { useDostupnostJednotkyQuery, useHzsSmenaAktualniQuery } from '../../api/JsdhNotifikaceService/service/DostupnostMutations';
import { useAllUsersQuery } from '../../api/JsdhNotifikaceService/service/AdminMutations';

const stavToText = (stav: DostupnostStav): string => {
    switch (stav) {
        case 'DOSTUPNY': return 'Dostupný';
        case 'OMEZENE_DOSTUPNY': return 'Omezeně dostupný';
        case 'NEDOSTUPNY': return 'Nedostupný';
        default: return stav;
    }
};

const stavToColor = (stav: DostupnostStav): 'success' | 'warning' | 'error' | 'default' => {
    switch (stav) {
        case 'DOSTUPNY': return 'success';
        case 'OMEZENE_DOSTUPNY': return 'warning';
        case 'NEDOSTUPNY': return 'error';
        default: return 'default';
    }
};

const formatDateTime = (value: string | undefined): string => {
    if (!value) return '';
    return new Date(value).toLocaleString('cs-CZ', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const formatSmartDate = (dateStr?: string): string => {
    if (!dateStr) return '\u2014';
    const d = dayjs(dateStr);
    if (d.hour() === 0 && d.minute() === 0) {
        return d.format('D. M. YYYY');
    }
    return d.format('D. M. YYYY HH:mm');
};

interface GroupConfig {
    stav: DostupnostStav;
    label: string;
    emoji: string;
    colorKey: 'success' | 'warning' | 'error';
}

const GROUPS: GroupConfig[] = [
    { stav: 'DOSTUPNY', label: 'Dostupní', emoji: '', colorKey: 'success' },
    { stav: 'OMEZENE_DOSTUPNY', label: 'Omezeně dostupní', emoji: '', colorKey: 'warning' },
    { stav: 'NEDOSTUPNY', label: 'Nedostupní', emoji: '', colorKey: 'error' },
];

const DostupnostPage: React.FC = () => {
    const { data: dostupnost, isLoading, error } = useDostupnostJednotkyQuery();
    const { data: allUsers } = useAllUsersQuery();
    const { data: hzsSmenaData } = useHzsSmenaAktualniQuery();
    const currentShift = hzsSmenaData?.currentShift || null;
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [viewMode, setViewMode] = useState<ViewMode>('cards');

    const stats = useMemo(() => {
        if (!dostupnost) return { dostupni: 0, omezene: 0, nedostupni: 0, naSmene: 0 };
        return {
            dostupni: dostupnost.filter(d => d.stav === 'DOSTUPNY').length,
            omezene: dostupnost.filter(d => d.stav === 'OMEZENE_DOSTUPNY').length,
            nedostupni: dostupnost.filter(d => d.stav === 'NEDOSTUPNY').length,
            naSmene: currentShift ? dostupnost.filter(d => d.hzsSmena === currentShift).length : 0,
        };
    }, [dostupnost, currentShift]);

    const nenastaveno = useMemo(() => {
        if (!allUsers || !dostupnost) return [];
        const dostupnostUserIds = new Set(dostupnost.map(d => d.uzivatelId));
        return allUsers
            .filter(u => u.aktivni && !dostupnostUserIds.has(u.id));
    }, [allUsers, dostupnost]);

    const grouped = useMemo(() => {
        if (!dostupnost) return new Map<DostupnostStav, Dostupnost[]>();
        const map = new Map<DostupnostStav, Dostupnost[]>();
        for (const group of GROUPS) {
            map.set(group.stav, dostupnost.filter(d => d.stav === group.stav));
        }
        return map;
    }, [dostupnost]);

    if (error) {
        return <Alert severity="error">Chyba při načítání dostupnosti: {error.message}</Alert>;
    }

    return (
        <Box>
            <PageHeader
                title="Dostupnost jednotky"
                action={<ViewToggle value={viewMode} onChange={setViewMode} />}
            />

            {currentShift && (
                <Alert severity="warning" icon={<FireIcon />} sx={{ mb: 2 }}>
                    Dnes slouží <strong>směna {currentShift}</strong>
                    {stats.naSmene > 0 && <> — {stats.naSmene} {stats.naSmene === 1 ? 'člen' : stats.naSmene < 5 ? 'členové' : 'členů'} na směně HZS (auto-NEJEDU)</>}
                </Alert>
            )}

            <Alert severity="info" sx={{ mb: 3 }}>
                Členové nastavují svou dostupnost v mobilní aplikaci (sekce Dostupnost).
                Nedostupní a omezeně dostupní členové automaticky dostanou odpověď <strong>NEJEDU</strong> při vyhlášení poplachu.
                Systém zasílá připomínky každých 7 dní a upozornění před vypršením platnosti.
            </Alert>

            {/* Summary chips */}
            {!isLoading && dostupnost && dostupnost.length > 0 && (
                <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                    <Chip label={`${stats.dostupni} dostupných`} color="success" variant="outlined" />
                    <Chip label={`${stats.omezene} omezeně`} color="warning" variant="outlined" />
                    <Chip label={`${stats.nedostupni} nedostupných`} color="error" variant="outlined" />
                    {stats.naSmene > 0 && (
                        <Chip label={`${stats.naSmene} na směně HZS`} color="info" variant="outlined" />
                    )}
                </Box>
            )}

            {isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
                    <CircularProgress />
                </Box>
            ) : !dostupnost || dostupnost.length === 0 ? (
                <Alert severity="info">Žádná data o dostupnosti.</Alert>
            ) : viewMode === 'cards' ? (
                /* Grouped cards view */
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {GROUPS.map((group) => {
                        const members = grouped.get(group.stav) || [];
                        return (
                            <Paper
                                key={group.stav}
                                variant="outlined"
                                sx={{
                                    borderLeft: 4,
                                    borderLeftColor: `${group.colorKey}.main`,
                                    overflow: 'hidden',
                                }}
                            >
                                <Box sx={{ px: 2, py: 1.5, bgcolor: 'action.hover' }}>
                                    <Typography variant="subtitle1" fontWeight={700}>
                                        {group.emoji} {group.label} ({members.length})
                                    </Typography>
                                </Box>
                                {members.length === 0 ? (
                                    <Box sx={{ px: 2, py: 2 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            Žádní členové
                                        </Typography>
                                    </Box>
                                ) : (
                                    <List disablePadding>
                                        {members.map((member) => (
                                            <ListItem
                                                key={member.id}
                                                divider
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: isMobile ? 'column' : 'row',
                                                    alignItems: isMobile ? 'flex-start' : 'center',
                                                    gap: isMobile ? 0.5 : 2,
                                                    py: 1.5,
                                                    px: 2,
                                                }}
                                            >
                                                <Box sx={{ flex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', minWidth: 0, flexWrap: 'wrap', gap: 0.5 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Typography variant="body2" fontWeight={600}>
                                                            {member.jmeno}
                                                        </Typography>
                                                        {member.funkce && (
                                                            <Typography variant="caption" color="text.secondary">{typeof member.funkce === 'string' ? member.funkce : Array.isArray(member.funkce) ? (member.funkce as any[]).map(f => typeof f === 'string' ? f : f?.kod).filter(Boolean).join(', ') : (member.funkce as any)?.kod || ''}</Typography>
                                                        )}
                                                        {member.specializace && (
                                                            <Typography variant="caption" color="text.secondary">{typeof member.specializace === 'string' ? member.specializace : Array.isArray(member.specializace) ? (member.specializace as any[]).map(s => typeof s === 'string' ? s : s?.kod).filter(Boolean).join(', ') : (member.specializace as any)?.kod || ''}</Typography>
                                                        )}
                                                        {member.hzsSmena && (
                                                            <Chip
                                                                label={member.hzsSmena}
                                                                size="small"
                                                                color={currentShift === member.hzsSmena ? 'error' : 'info'}
                                                                variant={currentShift === member.hzsSmena ? 'filled' : 'outlined'}
                                                                sx={{ fontWeight: 700, fontSize: '0.7rem', height: 18 }}
                                                            />
                                                        )}
                                                        {member.ztlumitNotifikace && (
                                                            <NotificationsOffIcon sx={{ fontSize: 16 }} color="warning" />
                                                        )}
                                                        {member.poznamka && (
                                                            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                                — {member.poznamka}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                    <Typography variant="caption" color="text.secondary" noWrap>
                                                        {member.platnostOd && `od ${formatSmartDate(member.platnostOd)}`}
                                                        {member.platnostOd && member.platnostDo && ' '}
                                                        {member.platnostDo && `do ${formatSmartDate(member.platnostDo)}`}
                                                    </Typography>
                                                </Box>
                                            </ListItem>
                                        ))}
                                    </List>
                                )}
                            </Paper>
                        );
                    })}

                    {/* Nenastaveno section */}
                    {nenastaveno.length > 0 && (
                        <Paper
                            variant="outlined"
                            sx={{
                                borderLeft: 4,
                                borderLeftColor: 'grey.400',
                                overflow: 'hidden',
                            }}
                        >
                            <Box sx={{ px: 2, py: 1.5, bgcolor: 'action.hover' }}>
                                <Typography variant="subtitle1" fontWeight={700}>
                                    Nenastaveno ({nenastaveno.length})
                                </Typography>
                            </Box>
                            <List disablePadding>
                                {nenastaveno.map((user) => (
                                    <ListItem
                                        key={user.id}
                                        divider
                                        sx={{ py: 1, px: 2 }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="body2" fontWeight={600}>
                                                {user.jmeno}
                                            </Typography>
                                            {user.funkce && (
                                                <Typography variant="caption" color="text.secondary">
                                                    {typeof user.funkce === 'string' ? user.funkce : Array.isArray(user.funkce) ? (user.funkce as any[]).map(f => typeof f === 'string' ? f : f?.kod).filter(Boolean).join(', ') : (user.funkce as any)?.kod || ''}
                                                </Typography>
                                            )}
                                            {user.specializace && (
                                                <Typography variant="caption" color="text.secondary">
                                                    {typeof user.specializace === 'string' ? user.specializace : Array.isArray(user.specializace) ? (user.specializace as any[]).map(s => typeof s === 'string' ? s : s?.kod).filter(Boolean).join(', ') : (user.specializace as any)?.kod || ''}
                                                </Typography>
                                            )}
                                        </Box>
                                    </ListItem>
                                ))}
                            </List>
                        </Paper>
                    )}
                </Box>
            ) : (
                /* Table view */
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Jméno</TableCell>
                                <TableCell>Funkce</TableCell>
                                <TableCell>Specializace</TableCell>
                                <TableCell align="center">HZS</TableCell>
                                <TableCell>Stav</TableCell>
                                <TableCell>Platnost od</TableCell>
                                <TableCell>Platnost do</TableCell>
                                <TableCell>Poznámka</TableCell>
                                <TableCell align="center">Ztlumeno</TableCell>
                                <TableCell>Poslední změna</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(dostupnost || []).map((row) => (
                                <TableRow key={row.id}>
                                    <TableCell>{row.jmeno}</TableCell>
                                    <TableCell>{typeof row.funkce === 'string' ? row.funkce : Array.isArray(row.funkce) ? (row.funkce as any[]).map(f => typeof f === 'string' ? f : f?.kod).filter(Boolean).join(', ') : (row.funkce as any)?.kod || ''}</TableCell>
                                    <TableCell>{typeof row.specializace === 'string' ? row.specializace : Array.isArray(row.specializace) ? (row.specializace as any[]).map(s => typeof s === 'string' ? s : s?.kod).filter(Boolean).join(', ') : (row.specializace as any)?.kod || ''}</TableCell>
                                    <TableCell align="center">
                                        {row.hzsSmena ? (
                                            <Chip
                                                label={row.hzsSmena}
                                                size="small"
                                                color={currentShift === row.hzsSmena ? 'error' : 'info'}
                                                variant={currentShift === row.hzsSmena ? 'filled' : 'outlined'}
                                                sx={{ fontWeight: 700, fontSize: '0.75rem' }}
                                            />
                                        ) : null}
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={stavToText(row.stav)}
                                            color={stavToColor(row.stav)}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>{formatSmartDate(row.platnostOd)}</TableCell>
                                    <TableCell>{formatSmartDate(row.platnostDo)}</TableCell>
                                    <TableCell>{row.poznamka}</TableCell>
                                    <TableCell align="center">
                                        {row.ztlumitNotifikace ? <NotificationsOffIcon fontSize="small" color="warning" /> : null}
                                    </TableCell>
                                    <TableCell>{formatDateTime(row.updatedAt)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
};

export default DostupnostPage;
